import React from "react";
import { Icon } from "../core/Icon";
import { DanyAvatar } from "./DanyAvatar";

/**
 * The streaming indicator. Four states, four distinct treatments — the SSE
 * contract emits all four and a single typing animation would flatten them
 * into "something is happening".
 *
 *   retrieving  scanning bar   "Looking through 40 FixyBees near 33012"
 *   generating  pulsing text   Dany is writing — the words are about to appear
 *   tool        spinning ring  Dany is doing something on your behalf (avatar: working)
 *   result      settled tick   The tool came back; this is what it returned
 */
const STATES = {
  retrieving: { icon: "search", fallback: "Looking through nearby FixyBees…" },
  generating: { icon: null, fallback: "Writing…" },
  tool: { icon: "settings-2", fallback: "Working on it…" },
  result: { icon: "check", fallback: "Done." },
};

export function DanyStatus({ state = "generating", label, detail, danyArt, showAvatar = true }) {
  const s = STATES[state] || STATES.generating;
  const text = label || s.fallback;

  const glyph =
    state === "retrieving" ? (
      <Icon name="search" size={15} style={{ color: "var(--dusk)" }} />
    ) : state === "tool" ? (
      <span style={{
        width: 15, height: 15, borderRadius: "var(--radius-pill)",
        border: "2px solid var(--hive-a35)", borderTopColor: "var(--hive-ink)",
        animation: "fixy-spin .8s linear infinite", display: "inline-block",
      }} />
    ) : state === "result" ? (
      <Icon name="check" size={15} style={{ color: "var(--success)" }} />
    ) : (
      <span style={{ display: "inline-flex", gap: 3 }}>
        {[0, 1, 2].map((i) => (
          <span key={i} style={{
            width: 5, height: 5, borderRadius: "var(--radius-pill)", background: "var(--hive-ink)",
            animation: `fixy-pulse 1.1s var(--ease-in-out) ${i * 0.16}s infinite`,
          }} />
        ))}
      </span>
    );

  const body = (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: "var(--space-2)",
      fontFamily: "var(--font-body)", fontSize: "var(--text-base)",
      color: state === "result" ? "var(--text-muted)" : "var(--dany-meta)",
      lineHeight: "var(--leading-normal)",
    }}>
      {glyph}
      {state === "retrieving" ? (
        <span style={{
          background: "linear-gradient(90deg, var(--smoke-light) 0%, var(--worker) 25%, var(--smoke-light) 50%)",
          backgroundSize: "200% 100%", WebkitBackgroundClip: "text", backgroundClip: "text",
          color: "transparent", animation: "fixy-scan 1.8s linear infinite",
        }}>{text}</span>
      ) : text}
      {detail && (
        <span style={{ fontFamily: "var(--font-mono)", fontSize: "var(--text-xs)", color: "var(--text-subtle)" }}>
          {detail}
        </span>
      )}
    </span>
  );

  if (!showAvatar) return body;
  return (
    <div style={{ display: "flex", gap: "var(--space-3)", alignItems: "center" }}>
      <DanyAvatar size={28} src={danyArt} state={state === "result" ? "happy" : state === "generating" ? "speaking" : state === "tool" ? "working" : "thinking"} />
      {body}
    </div>
  );
}
